import { useState, useEffect, useContext, useRef, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload,
  Business,
  People,
  Security,
  Payment,
  CheckCircle,
  Warning,
  Stars,
  DeleteOutlined as Delete,
} from "@mui/icons-material";
import {
  fetchOrganisationProfile,
  updateOrganisation,
  uploadOrganisationLogo,
  deleteOrganisationLogo,
} from "../services/organisations.service";
import { useAlert } from "../functions/useAlert";
import { DataContext } from "../contexts/DataProvider";
import { useNavigate } from "react-router-dom";
import AgentModelSettingsCard from "./components/AgentModelSettingsCard";

// ─── Validation constants (same rules used across the app) ───────────────────
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(\+61[0-9]{9}|0[0-9]{9}|\+?[0-9]{10,15})$/;
const isValidPhone = (value: string): boolean =>
  PHONE_REGEX.test(value.replace(/[\s\-\(\)]/g, ""));
const ABN_REGEX = /^\d{11}$/;
// ADD this after ABN_REGEX at the top:
const WEBSITE_REGEX = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,6}(\/[-a-zA-Z0-9@:%_+.~#?&/=]*)?$/;
// ─────────────────────────────────────────────────────────────────────────────

// Type definitions
interface BillingAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface SecurityPolicies {
  require2FA: boolean;
}

interface SubscriptionStatus {
  status: string;
  plan?: string;
  expiresAt?: string;
}

interface OrganisationProfileData {
  name: string;
  website: string;
  abn: string;
  afslNumber: string;
  logoUrl: string;
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;
  billingAddress: BillingAddress;
  securityPolicies: SecurityPolicies;
  isBillingExempt?: boolean;
}

interface OrganisationResponse {
  success: boolean;
  organisation: OrganisationProfileData & {
    activeUserCount: number;
    subscriptionStatus: SubscriptionStatus;
  };
  error?: string;
}

interface ApiResponse {
  success: boolean;
  logoUrl?: string;
  error?: string;
}

interface FormErrors {
  name?: string;
  billingContactEmail?: string;
    billingContactName?: string;
  billingContactPhone?: string; // ← ADDED
  abn?: string;                 // ← ADDED
   website?: string; 
  [key: string]: string | undefined;
}

const OrganisationProfile = ({ orgId }: { orgId: string }) => {
  const { login } = useContext(DataContext);
  const [setAlert] = useAlert();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State for form data
  const [profileData, setProfileData] = useState<OrganisationProfileData>({
    name: "",
    website: "",
    abn: "",
    afslNumber: "",
    logoUrl: "",
    billingContactName: "",
    billingContactEmail: "",
    billingContactPhone: "",
    billingAddress: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    securityPolicies: {
      require2FA: false,
    },
    isBillingExempt: false,
  });
const [isDirty, setIsDirty] = useState<boolean>(false);
  // UI state
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [activeUserCount, setActiveUserCount] = useState<number>(0);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [originalData, setOriginalData] =
    useState<OrganisationProfileData | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  // Load organisation profile data
  const loadOrganisationProfile = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      // For global admin: use orgId from dropdown
      // For org admin: always use login.data.orgId
      const isGlobalAdmin = login?.data?.role === "global admin";
      const effectiveOrgId = isGlobalAdmin ? orgId : login?.data?.orgId;

      if (!effectiveOrgId) {
        setAlert("Organisation ID not found", "error");
        return;
      }

      const response = (await fetchOrganisationProfile(
        effectiveOrgId,
      )) as OrganisationResponse;
      if (response.success) {
        const org = response.organisation;
        const profileDataObj = {
          name: org.name || "",
          website: org.website || "",
          abn: org.abn || "",
          afslNumber: org.afslNumber || "",
          logoUrl: org.logoUrl || "",
          billingContactName: org.billingContactName || "",
          billingContactEmail: org.billingContactEmail || "",
          billingContactPhone: org.billingContactPhone || "",
          isBillingExempt: org.isBillingExempt || false,
          billingAddress: {
            line1: org.billingAddress?.line1 || "",
            line2: org.billingAddress?.line2 || "",
            city: org.billingAddress?.city || "",
            state: org.billingAddress?.state || "",
            zipCode: org.billingAddress?.zipCode || "",
            country: org.billingAddress?.country || "",
          },
          securityPolicies: {
            require2FA: org.securityPolicies?.require2FA || false,
          },
        };
        setProfileData(profileDataObj);
        setOriginalData(profileDataObj); // Store original data for comparison
        setHasUnsavedChanges(false); // Reset unsaved changes flag
        setIsDirty(false);
        setActiveUserCount(org.activeUserCount || 0);
        setSubscriptionStatus(org.subscriptionStatus);
      } else {
        setAlert("Failed to load organisation profile", "error");
      }
    } catch (error) {
      console.error("Error loading organisation profile:", error);
      setAlert("Error loading organisation profile", "error");
    } finally {
      setLoading(false);
    }
  }, [orgId, login?.data?.orgId, login?.data?.role, setAlert]);

  // Check if current data differs from original
const hasDataChanged = useCallback((): boolean => {
    if (!originalData) return true; // ← treat as changed if original hasn't loaded yet
    return JSON.stringify(profileData) !== JSON.stringify(originalData);
  }, [profileData, originalData]);


  // Update unsaved changes flag whenever data changes
  useEffect(() => {
    setHasUnsavedChanges(hasDataChanged());
  }, [hasDataChanged]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: string | boolean): void => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      if (parent === "billingAddress") {
        setProfileData((prev) => ({
          ...prev,
          billingAddress: {
            ...prev.billingAddress,
            [child]: value as string,
          },
        }));
      } else if (parent === "securityPolicies") {
        setProfileData((prev) => ({
          ...prev,
          securityPolicies: {
            ...prev.securityPolicies,
            [child]: value as boolean,
          },
        }));
      }
    } else {
      setProfileData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
    setIsDirty(true);
  };

  // ─── UPDATED: validateForm — added phone and ABN validation ─────────────────
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Organisation name — required (unchanged)
    if (!profileData.name?.trim()) {
      newErrors.name = "Organisation name is required";
    } else if (!/[a-zA-Z]/.test(profileData.name.trim())) {
      newErrors.name = "Organisation name cannot be purely numerical";
    }
   if (
      profileData.billingContactName?.trim() &&
      !/[a-zA-Z]/.test(profileData.billingContactName.trim())
    ) {
      newErrors.billingContactName =
        "Billing contact name cannot be purely numerical";
    }
     if (
      profileData.website?.trim() &&
      !WEBSITE_REGEX.test(profileData.website.trim())
    ) {
      newErrors.website = "Please enter a valid website URL (e.g. https://example.com)";
    }
    // Billing contact email — required + format (email validation ADDED)
    if (!profileData.billingContactEmail?.trim()) {
      newErrors.billingContactEmail = "Billing contact email is required";
    } else if (!EMAIL_REGEX.test(profileData.billingContactEmail)) {
      newErrors.billingContactEmail = "Please enter a valid email address";
    }

    // Phone — format only (not required, ADDED)
   if (
      profileData.billingContactPhone?.trim() &&
      !isValidPhone(profileData.billingContactPhone)
    ) {
      newErrors.billingContactPhone =
        "Enter a valid phone number (e.g. +61412345678 or 0412345678)";
    }

    // ABN — exactly 11 digits if provided (ADDED)
   // ABN — exactly 11 digits if provided
const cleanABN = profileData.abn?.replace(/\s/g, "") || "";

if (cleanABN.length > 0) {
  if (cleanABN.length !== 11 || !ABN_REGEX.test(cleanABN)) {
    newErrors.abn = "ABN must be exactly 11 digits (numbers only)";
  }
}

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // ─────────────────────────────────────────────────────────────────────────────

  // Save organisation profile
  const handleSave = async (): Promise<void> => {
    if (!validateForm()) {
      setAlert("Please fix the errors before saving", "error");
      return;
    }

    try {
      setSaving(true);
      // For global admin: use orgId from dropdown
      // For org admin: always use login.data.orgId
      const isGlobalAdmin = login?.data?.role === "global admin";
      const effectiveOrgId = isGlobalAdmin ? orgId : login?.data?.orgId;

      const response = (await updateOrganisation(
        effectiveOrgId,
        profileData as unknown as Record<string, unknown>,
      )) as { success: boolean; error?: string };

      if (response.success) {
        setAlert("Organisation profile updated successfully", "success");
        setOriginalData(profileData); // Update original data after successful save
        setHasUnsavedChanges(false); // Reset unsaved changes flag
        setIsDirty(false);
      } else {
        setAlert(
          response.error || "Failed to update organisation profile",
          "error",
        );
      }
    } catch (error) {
      console.error("Error saving organisation profile:", error);
      setAlert("Error saving organisation profile", "error");
    } finally {
      setSaving(false);
    }
  };

  // Handle logo file selection
  const handleLogoFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith("image/")) {
      setAlert("Please upload an image file", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      // 5MB
      setAlert("Image size should be less than 5MB", "error");
      return;
    }

    uploadLogo(file);
  };

  // Handle logo upload
  const uploadLogo = async (file: File): Promise<void> => {
    try {
      setUploadingLogo(true);
      setUploadProgress(0);
      // For global admin: use orgId from dropdown
      // For org admin: always use login.data.orgId
      const isGlobalAdmin = login?.data?.role === "global admin";
      const effectiveOrgId = isGlobalAdmin ? orgId : login?.data?.orgId;

      const formData = new FormData();
      formData.append("logo", file);

      // Simulate progress for better UX (since FormData upload progress is complex)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = (await uploadOrganisationLogo(
        effectiveOrgId,
        formData,
      )) as ApiResponse;

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success && response.logoUrl) {
        setProfileData((prev) => ({
          ...prev,
          logoUrl: response.logoUrl || "",
        }));
        setAlert("Logo uploaded successfully", "success");
      } else {
        setAlert(response.error || "Failed to upload logo", "error");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      setAlert("Error uploading logo", "error");
    } finally {
      setUploadingLogo(false);
      setUploadProgress(0);
    }
  };

  // Handle logo deletion
  const deleteLogo = async (): Promise<void> => {
    try {
      setUploadingLogo(true);
      // For global admin: use orgId from dropdown
      // For org admin: always use login.data.orgId
      const isGlobalAdmin = login?.data?.role === "global admin";
      const effectiveOrgId = isGlobalAdmin ? orgId : login?.data?.orgId;

      const response = (await deleteOrganisationLogo(
        effectiveOrgId,
      )) as { success: boolean; error?: string };

      if (response.success) {
        setProfileData((prev) => ({
          ...prev,
          logoUrl: "",
        }));
        setAlert("Logo deleted successfully", "success");
        setShowDeleteConfirm(false);
      } else {
        setAlert(response.error || "Failed to delete logo", "error");
      }
    } catch (error) {
      console.error("Error deleting logo:", error);
      setAlert("Error deleting logo", "error");
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle drag and drop events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const file = files[0];

    if (file && file.type.startsWith("image/")) {
      if (file.size > 5 * 1024 * 1024) {
        setAlert("Image size should be less than 5MB", "error");
        return;
      }
      uploadLogo(file);
    } else {
      setAlert("Please drop an image file", "error");
    }
  };

  const navigate = useNavigate();

  // Add unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        return "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (hasUnsavedChanges) {
        const confirmLeave = window.confirm(
          "You have unsaved changes. Are you sure you want to leave?",
        );
        if (!confirmLeave) {
          e.preventDefault();
          // Push the current state back to prevent navigation
          window.history.pushState(null, "", window.location.href);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedChanges]);

  // Navigate to payments tab - this will be handled by parent component
  const goToPayments = (): void => {
    console.log("Navigating to payments tab...");
    navigate("/admin?tab=payments");
  };

  useEffect(() => {
    loadOrganisationProfile();
  }, [loadOrganisationProfile]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Unsaved Changes Indicator */}
      {hasUnsavedChanges && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Warning color="warning" fontSize="small" />
          <Typography variant="caption" color="warning.main">
            You have unsaved changes
          </Typography>
        </Box>
      )}

      {/* Section 1: General Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Business sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h6" component="h2">
              General Information
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Logo Upload */}
            <Grid item xs={12} md={6}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ minHeight: 28, mb: 1 }} // Fixed height for label area
              >
                <Typography variant="subtitle2">Organisation Logo</Typography>
                {profileData.logoUrl && (
                  <Tooltip title="Delete Logo" placement="top">
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(true);
                      }}
                      size="small"
                      sx={{
                        backgroundColor: "error.50",
                        "&:hover": {
                          backgroundColor: "error.100",
                        },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Paper
                sx={{
                  p: 3,
                  border: "2px dashed",
                  borderColor: "grey.300",
                  backgroundColor: "grey.50",
                  cursor: "pointer",
                  textAlign: "center",
                  position: "relative",
                  height: 180, // Fixed height
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "primary.50",
                  },
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoFileSelect}
                  accept="image/*"
                  style={{ display: "none" }}
                />

                {uploadingLogo ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={24} sx={{ mb: 1 }} />
                    {uploadProgress > 0 && (
                      <Box sx={{ width: "100%", mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: "center",
                            display: "block",
                            mt: 0.5,
                          }}
                        >
                          Uploading... {uploadProgress}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : profileData.logoUrl ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 200,
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <img
                        src={profileData.logoUrl}
                        alt="organisation Logo"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ textAlign: "center" }}
                    >
                      Click or drag to change logo
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CloudUpload
                      sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                    />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textAlign: "center" }}
                    >
                      Drag & drop an image, or click to select
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ textAlign: "center" }}
                    >
                      Max 5MB • JPG, PNG, GIF
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Active Users */}
            <Grid item xs={12} md={6}>
              <Box sx={{ minHeight: 28, mb: 1 }}>
                <Typography variant="subtitle2">Active Users</Typography>
              </Box>
              <Paper
                sx={{
                  p: 3,
                  pb: 0.2,
                  backgroundColor: "primary.50",
                  textAlign: "center",
                  height: 180, // Fixed height to match Logo card
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <People sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h3" color="primary.main">
                    {activeUserCount}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="primary.main"
                  fontWeight="medium"
                >
                  ACTIVE USERS
                </Typography>
              </Paper>
            </Grid>

            {/* Organisation Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Organisation Name"
                value={profileData.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            {/* Website */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={profileData.website}
                onChange={(e) => handleFieldChange("website", e.target.value)}
                error={!!errors.website}
                helperText={errors.website || "e.g. https://example.com"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="textSecondary">
                        https://
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* ABN — UPDATED: added error + helperText for validation feedback */}
            <Grid item xs={12} md={6}>
             <TextField
  fullWidth
  label="ABN"
  value={profileData.abn}
  onChange={(e) => handleFieldChange("abn", e.target.value)}
  error={
    !!errors.abn ||
    (profileData.abn?.replace(/\s/g, "").length > 0 &&
      profileData.abn?.replace(/\s/g, "").length < 11)
  }
  helperText={
    errors.abn ||
    (profileData.abn?.replace(/\s/g, "").length > 0 &&
    profileData.abn?.replace(/\s/g, "").length < 11
      ? `${profileData.abn?.replace(/\s/g, "").length}/11 digits — ABN must be exactly 11 digits`
      : "Australian Business Number (11 digits)")
  }
  inputProps={{ maxLength: 11 }}
/>
            </Grid>

            {/* AFSL Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="AFSL Number"
                value={profileData.afslNumber}
                onChange={(e) =>
                  handleFieldChange("afslNumber", e.target.value)
                }
                helperText="Australian Financial Services License Number"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <AgentModelSettingsCard orgId={orgId} />

      {/* Section 2: Billing & Contact Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Payment sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="h6" component="h2">
                Billing & Contact Details
              </Typography>
            </Box>

            {/* Subscription Status */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {profileData.isBillingExempt ? (
                <>
                  <Chip
                    icon={<Stars />}
                    label="Partner Program Access"
                    color="primary"
                    size="small"
                    sx={{
                      backgroundColor: "#6f42c1",
                      color: "white",
                      "& .MuiChip-icon": {
                        color: "white",
                      },
                    }}
                  />
                </>
              ) : subscriptionStatus?.status === "active" ? (
                <>
                  <Chip
                    icon={<CheckCircle />}
                    label="Subscription Active"
                    color="success"
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={goToPayments}
                  >
                    View Plans
                  </Button>
                </>
              ) : (
                <>
                  <Chip
                    icon={<Warning />}
                    label="Payment Required"
                    color="warning"
                    size="small"
                  />
                  <Button
                    variant="contained"
                    size="small"
                    color="warning"
                    onClick={goToPayments}
                  >
                    Go to Payment Page
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Billing Contact Name */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Billing Contact Name"
                value={profileData.billingContactName}
                onChange={(e) =>
                  handleFieldChange("billingContactName", e.target.value)
                }
                error={!!errors.billingContactName}
                helperText={errors.billingContactName}
              />
            </Grid>

            {/* Billing Contact Email — UPDATED: error + helperText wired to validation */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Billing Contact Email"
                value={profileData.billingContactEmail}
                onChange={(e) =>
                  handleFieldChange("billingContactEmail", e.target.value)
                }
                error={!!errors.billingContactEmail}
                helperText={errors.billingContactEmail}
                required
              />
            </Grid>

            {/* Contact Number — UPDATED: added error + helperText for phone validation */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contact Number"
                value={profileData.billingContactPhone}
                onChange={(e) =>
                  handleFieldChange("billingContactPhone", e.target.value)
                }
                error={!!errors.billingContactPhone}
                helperText={
                  errors.billingContactPhone ||
                  "Digits, +, -, and spaces only"
                }
              />
            </Grid>
          </Grid>

          {/* Billing Address Section */}
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Business sx={{ mr: 0.5, fontSize: 18 }} />
              BILLING ADDRESS
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Address Line 1 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={profileData.billingAddress.line1}
                  onChange={(e) =>
                    handleFieldChange("billingAddress.line1", e.target.value)
                  }
                />
              </Grid>

              {/* Address Line 2 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={profileData.billingAddress.line2}
                  onChange={(e) =>
                    handleFieldChange("billingAddress.line2", e.target.value)
                  }
                  placeholder="Suite, Unit, Building, Floor, etc."
                />
              </Grid>

              {/* City & State */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={profileData.billingAddress.city}
                  onChange={(e) =>
                    handleFieldChange("billingAddress.city", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/Province"
                  value={profileData.billingAddress.state}
                  onChange={(e) =>
                    handleFieldChange("billingAddress.state", e.target.value)
                  }
                />
              </Grid>

              {/* ZIP & Country */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ZIP/Postal Code"
                  value={profileData.billingAddress.zipCode}
                  onChange={(e) =>
                    handleFieldChange("billingAddress.zipCode", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={profileData.billingAddress.country}
                  onChange={(e) =>
                    handleFieldChange("billingAddress.country", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Section 3: Security Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Security sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h6" component="h2">
              Security Preferences
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={profileData.securityPolicies.require2FA}
                onChange={(e) =>
                  handleFieldChange(
                    "securityPolicies.require2FA",
                    e.target.checked,
                  )
                }
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Require Two-Factor Authentication (2FA)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  When enabled, all users within this organisation will be
                  required to configure and use 2FA to sign in.
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => {
            if (hasUnsavedChanges) {
              const confirmReset = window.confirm(
                "You have unsaved changes. Are you sure you want to reset all changes?",
              );
              if (confirmReset) {
                loadOrganisationProfile();
              }
            } else {
              loadOrganisationProfile();
            }
          }}
          disabled={saving}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
       disabled={saving || !isDirty}
          startIcon={saving && <CircularProgress size={18} />}
        
       color={isDirty ? "primary" : "inherit"}
>
  {saving ? "Saving..." : isDirty ? "Save Changes*" : "Save Changes"}
        </Button>
      </Box>

      {/* Logo Deletion Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>Delete Logo</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the organisation logo? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button
            onClick={deleteLogo}
            color="error"
            variant="contained"
            disabled={uploadingLogo}
          >
            {uploadingLogo ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganisationProfile;
